import {useEffect, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import {getRecommendedSeries, getSeriesGenres, getSeriesByGenre, getGenres} from '../../api';
import HorizontalScroll from '../../common/HorizontalScroll';
import ResponsiveContainer from '../../common/ResponsiveContainer';
import useIsInViewport from '../../hooks/isInViewport';
import './Recommendations.css';

const Recommendations = () => {
    const [mostRecentEpisodes, setMostRecentEpisodes] = useState([]);
    const [mostRecentSeries, setMostRecentSeries] = useState([]);
    const [genresData, setGenresData] = useState([]);
    const [genres, setGenres] = useState([]);
    const [loadedGenres, setLoadedGenres] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const endBarRef = useRef(null);
    const isEndBarVisible = useIsInViewport(endBarRef);

    const loadMore = async () => {
        try {
            setLoading(true);
            const genre = genres[loadedGenres];
            setLoadedGenres(loadedGenres + 1);

            const response = await getSeriesByGenre(genre._id);

            if (response.data.length >= 6) {
                const _genresData = {...genresData};
                _genresData[genre.name] = response.data;
                setGenresData(_genresData);
            }
            setLoading(false);
        } catch (err) {
            console.log(err);
        }
    };

    useEffect(() => {
        getRecommendedSeries()
            .then((res) => {
                setMostRecentEpisodes(res.data.episodes);
                setMostRecentSeries(res.data.series);
            })
            .catch((err) => {
                setError(err?.response?.status === 403 ? 'forbidden' : 'error');
            });

        getGenres().then((res) => {
            setGenres(res.data.sort((a, b) => a.name.localeCompare(b.name)));
        });
    }, []);

    useEffect(() => {
        if (isEndBarVisible && !loading && loadedGenres < genres.length && genres.length > 0) {
            loadMore();
        }
    }, [isEndBarVisible, loading, genres]);

    if (error === 'forbidden') {
        return (
            <ResponsiveContainer className='flex flex-col md:px-2'>
                <div className='flex flex-col items-center justify-center min-h-[60vh] text-center gap-4'>
                    <p className='text-5xl'>🔒</p>
                    <h2 className='font-montserrat font-bold text-xl text-gray-800'>
                        No tienes acceso a este contenido
                    </h2>
                    <p className='font-montserrat text-gray-500 text-sm max-w-sm'>
                        Para acceder al contenido necesitas ser miembro. Apoya el proyecto a través de una donación.
                    </p>
                    <Link
                        to='/donaciones'
                        className='inline-flex items-center gap-2 bg-red-1 hover:bg-darkred text-white font-montserrat font-semibold px-6 py-2.5 rounded-lg transition-colors'
                    >
                        Ver opciones de donación
                    </Link>
                </div>
            </ResponsiveContainer>
        );
    }

    return (
        <>
            <ResponsiveContainer className='flex flex-col md:px-2'>
                {/* Ultimos capitulos actualizados */}

                <div className='p-2 rounded-lg bg-white shadow mt-4'>
                    <h2 className='font-montserrat font-semibold md:text-2xl text-xl pb-2 text-red-1'>Últimos capítulos actualizados</h2>
                    <div className='flex flex-row w-full'>
                        <HorizontalScroll data={mostRecentEpisodes} episode/>
                    </div>
                </div>

                {/* Ultimas series actualizadas */}
                <div className='p-2 rounded-lg bg-white shadow mt-4'>
                    <h2 className='font-montserrat font-semibold md:text-2xl text-xl pb-2 text-red-1'>Últimas series actualizadas</h2>
                    <div className='flex flex-row'>
                        <HorizontalScroll data={mostRecentSeries} serie/>
                    </div>
                </div>
                {/* Continuar viendo */}

                {/* Generos */}
                {Object.keys(genresData)
                    .sort((a, b) => a.localeCompare(b))
                    .map((genre, index) => {
                        const data = genresData[genre];
                        if (data.length === 0) return null;
                        return (
                            <div key={index} className='p-2 rounded-lg bg-white shadow mt-4'>
                                <h2 className='font-montserrat font-semibold md:text-2xl text-xl pb-2 text-red-1'>{genre}</h2>
                                <div className='flex flex-row '>
                                    <HorizontalScroll data={data} serie/>
                                </div>
                            </div>
                        );
                    })}
                {loading && <div className='flex justify-center h-14 w-full'><span className='loader'/></div>}
                {!loading && loadedGenres >= genres.length && genres.length > 0 && (
                    <p className='font-montserrat text-center text-gray-500 my-6'>
                        Si quieres ver más opciones ve a <Link to='/series' className='text-red-1 font-semibold hover:underline'>Series</Link>
                    </p>
                )}
                <div ref={endBarRef} className='opacity-0 w-full h-4 relative bottom-[20rem] bg-red-200 z-50'/>
            </ResponsiveContainer>
        </>
    );
};

export default Recommendations;
